import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  CircularProgress,
  Typography,
  Paper,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { updatePublisher, createPublisher } from 'features/publishers/slices/publishersSlice';
import { AppDispatch } from 'store';
import { Publisher, PublisherFormData } from 'types/publisher.types';
import LocationSelector from 'features/locations/LocationSelector';
import ImageUrlSelector from 'components/common/ImageUrlSelector';
import apiClient from 'api/apiClient';
import { API_ENDPOINTS } from 'api/endPoints';

interface PublisherFormProps {
  open: boolean;
  onClose: () => void;
  publisher?: Publisher;
  isEdit?: boolean;
  onPublisherCreated?: (publisher: Publisher) => void;
}

const initialFormData: PublisherFormData = {
  name: '',
  tagline: '',
  businessAddress: {
    street: '',
    city: '',
    state: '',
    zip: '',
  },
  logoUrl: '',
  websiteUrl: '',
  foundedYear: new Date().getFullYear(),
  primaryLocationPK: '',
  // Preserve IssueCast by default so CRUD round-trips safely
  issueCast: {
    enabled: false,
    masthead: {
      publisherLogoURL: '',
      publisherSubdomain: '',
    }
  }
};

const PublisherForm: React.FC<PublisherFormProps> = ({
  open,
  onClose,
  publisher,
  isEdit = false,
  onPublisherCreated,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [formData, setFormData] = useState<PublisherFormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [rssArticleCount, setRssArticleCount] = useState<number | ''>(3);
  const [createMode, setCreateMode] = useState<'name' | 'website'>('name');

  // Populate form with publisher data if in edit mode
  useEffect(() => {
    if (isEdit && publisher) {
      setFormData({
        name: publisher.name,
        tagline: publisher.tagline || '',
        businessAddress: {
          street: publisher.businessAddress?.street || '',
          city: publisher.businessAddress?.city || '',
          state: publisher.businessAddress?.state || '',
          zip: publisher.businessAddress?.zip || '',
        },
        logoUrl: publisher.logoUrl || '',
        websiteUrl: publisher.websiteUrl || '',
        foundedYear: publisher.foundedYear || new Date().getFullYear(),
        primaryLocationPK: publisher.primaryLocationPK || '',
        // Ensure IssueCast config is preserved during edits
        issueCast: publisher.issueCast || initialFormData.issueCast,
      });
      setRssArticleCount(3);
      setCreateMode('name');
    } else {
      setFormData(initialFormData);
      setRssArticleCount(3);
      setCreateMode(initialFormData.websiteUrl.trim().length > 0 ? 'website' : 'name');
    }
  }, [isEdit, publisher]);

  const isValidUrlOrFqdn = (value: string): boolean => {
    const trimmed = value.trim();
    if (!trimmed) {
      return false;
    }

    const fqdnRegex = /^(?=.{1,253}$)(?!-)(?:[a-zA-Z0-9-]{1,63}\.)+[A-Za-z]{2,63}$/;
    if (fqdnRegex.test(trimmed)) {
      return true;
    }

    const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed);
    const candidate = hasScheme ? trimmed : `https://${trimmed}`;

    try {
      const parsed = new URL(candidate);
      return fqdnRegex.test(parsed.hostname);
    } catch {
      return false;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name.includes('.')) {
      // Handle nested object (address fields or issueCast fields)
      const keys = name.split('.');
      setFormData(prev => {
        const updated: any = { ...prev };
        let ref: any = updated;
        for (let i = 0; i < keys.length - 1; i++) {
          const k = keys[i];
          ref[k] = ref[k] ? { ...ref[k] } : {};
          ref = ref[k];
        }
        ref[keys[keys.length - 1]] = value;
        return updated as PublisherFormData;
      });
    } else {
      // Handle root level fields
      let parsedValue: string | number = value;

      // Convert numeric fields
      if (name === 'foundedYear') {
        parsedValue = value ? Number(value) : new Date().getFullYear();
      }

      setFormData({
        ...formData,
        [name]: parsedValue,
      });
    }

    // Clear error when field is edited
    if (errors[name] || (name === 'websiteUrl' && errors['numArticles'])) {
      setErrors({
        ...errors,
        [name]: undefined,
        ...(name === 'websiteUrl' ? { numArticles: undefined } : {}),
      });
    }
  };

  const handleRssArticleCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const parsedValue = value === '' ? '' : Number(value);

    setRssArticleCount(parsedValue);

    if (errors['numArticles']) {
      setErrors({
        ...errors,
        numArticles: undefined,
      });
    }
  };

  const handleCreateModeChange = (_event: React.MouseEvent<HTMLElement>, nextMode: 'name' | 'website' | null) => {
    if (!nextMode) {
      return;
    }

    setCreateMode(nextMode);
    if (nextMode === 'name') {
      setFormData(prev => ({ ...prev, websiteUrl: '' }));
      setRssArticleCount(3);
      setErrors(prev => ({
        ...prev,
        websiteUrl: undefined,
        numArticles: undefined,
      }));
    } else {
      setFormData(prev => ({ ...prev, name: '' }));
      setErrors(prev => ({
        ...prev,
        name: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<string, string>> = {};
    const trimmedName = formData.name.trim();
    const trimmedWebsiteUrl = formData.websiteUrl.trim();
    const hasName = trimmedName.length > 0;
    const hasWebsiteUrl = !isEdit && trimmedWebsiteUrl.length > 0;
    const websiteUrlIsValid = hasWebsiteUrl ? isValidUrlOrFqdn(trimmedWebsiteUrl) : false;
    const parsedNumArticles =
      rssArticleCount === '' ? NaN : Number(rssArticleCount);

    if (!hasName && !hasWebsiteUrl) {
      newErrors['name'] = 'Enter a publisher name or a website URL';
      newErrors['websiteUrl'] = 'Enter a publisher name or a website URL';
    }

    if (hasName && hasWebsiteUrl) {
      newErrors['name'] = 'Choose either a publisher name or a website URL, not both';
      newErrors['websiteUrl'] = 'Choose either a publisher name or a website URL, not both';
    }

    if (hasWebsiteUrl && !websiteUrlIsValid) {
      newErrors['websiteUrl'] = 'Enter a valid URL or fully qualified domain name';
    }

    if (hasWebsiteUrl && websiteUrlIsValid) {
      if (!Number.isInteger(parsedNumArticles) || parsedNumArticles < 1 || parsedNumArticles > 20) {
        newErrors['numArticles'] = 'Enter a whole number between 1 and 20';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      if (isEdit && publisher) {
        await dispatch(updatePublisher({ id: publisher.id, data: formData }));
        onClose();
      } else {
        const trimmedWebsiteUrl = formData.websiteUrl.trim();
        const hasWebsiteUrl = trimmedWebsiteUrl.length > 0;
        const hasName = formData.name.trim().length > 0;
        const websiteUrlIsValid = hasWebsiteUrl && isValidUrlOrFqdn(trimmedWebsiteUrl);
        const parsedNumArticles =
          rssArticleCount === '' ? NaN : Number(rssArticleCount);

        if (!hasName && websiteUrlIsValid && Number.isInteger(parsedNumArticles)) {
          await apiClient.post(API_ENDPOINTS.PUBLISHER.PUBLISHERS.WIZARD_CREATE_PODCAST_FROM_RSS, {
            'website-url': trimmedWebsiteUrl,
            numArticles: parsedNumArticles,
            name: formData.name,
          });
          onClose();
        } else {
          const created = await dispatch(createPublisher(formData)).unwrap();
          onClose();
          if (onPublisherCreated) {
            onPublisherCreated(created as Publisher);
          }
        }
      }
    } catch (error) {
      console.error('Error saving publisher:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>{isEdit ? 'Edit Publisher' : 'Add New Publisher'}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 2 }}>
          <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Publisher Information
            </Typography>
            {/* Help text varies for create vs. edit */}
            {!isEdit ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter either a Publisher Name or a Website URL. Name-only creation requires manual setup; Website URL creation runs automated setup and you can edit later.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the basic information about the publisher organization.
              </Typography>
            )}

            <Grid container spacing={2}>
              {!isEdit && (
                <Grid item xs={12}>
                  <ToggleButtonGroup
                    value={createMode}
                    exclusive
                    onChange={handleCreateModeChange}
                    aria-label="publisher creation mode"
                    disabled={isLoading}
                  >
                    <ToggleButton value="name" aria-label="create with name">
                      Create with name
                    </ToggleButton>
                    <ToggleButton value="website" aria-label="create with website">
                      Create with website
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Name-only setup is manual. Website URL setup is automated and can be edited later.
                  </Typography>
                </Grid>
              )}

              {/* Always show Publisher Name */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Publisher Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  error={!!errors['name']}
                  helperText={errors['name'] || 'Leave blank if you are using a Website URL.'}
                  disabled={isLoading || (!isEdit && createMode === 'website')}
                />
              </Grid>

              {!isEdit && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Website URL"
                      name="websiteUrl"
                      value={formData.websiteUrl}
                      onChange={handleInputChange}
                      error={!!errors['websiteUrl']}
                      helperText={errors['websiteUrl'] || 'Enter a website URL or RSS feed URL.'}
                      disabled={isLoading || createMode === 'name'}
                      placeholder="e.g., https://www.example.com or example.com"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Articles to import from RSS"
                      name="numArticles"
                      type="number"
                      value={rssArticleCount}
                      onChange={handleRssArticleCountChange}
                      error={!!errors['numArticles']}
                      helperText={errors['numArticles'] || '1-20 articles (default 3)'}
                      disabled={isLoading || createMode === 'name' || !isValidUrlOrFqdn(formData.websiteUrl)}
                      InputProps={{ inputProps: { min: 1, max: 20 } }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Enter either a Publisher Name or a Website URL (not both). If you provide a Website URL, it must have an RSS feed. You can also paste the RSS feed URL directly.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Name-only creation requires manual setup later. Website URL creation runs an automated job that:
                    </Typography>
                    <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3, color: 'text.secondary' }}>
                      <li>Create a new Publisher and Website</li>
                      <li>Find the RSS feed if needed</li>
                      <li>Run demographic research</li>
                      <li>Import and categorize articles</li>
                      <li>Generate an audio style guide</li>
                      <li>Create two cohost personalities</li>
                      <li>Assign voice models</li>
                      <li>Create a podcast from the articles</li>
                      <li>Publish a new IssueCast for review</li>
                    </Box>
                  </Grid>
                </>
              )}

              {/* Show all other fields only in edit mode */}
              {isEdit && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Tagline"
                      name="tagline"
                      value={formData.tagline}
                      onChange={handleInputChange}
                      error={!!errors['tagline']}
                      helperText={errors['tagline']}
                      disabled={isLoading}
                      placeholder="e.g., San Francisco's premier news source"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Website URL"
                      name="websiteUrl"
                      value={formData.websiteUrl}
                      onChange={handleInputChange}
                      error={!!errors['websiteUrl']}
                      helperText={errors['websiteUrl']}
                      disabled={isLoading}
                      placeholder="e.g., https://www.example.com"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <ImageUrlSelector
                      label="Logo URL"
                      name="logoUrl"
                      value={formData.logoUrl}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      publisherId={publisher?.id}
                      placeholder="e.g., https://s3.amazonaws.com/location-station/logos/publisher.png"
                      helperText={errors['logoUrl'] || ''}
                      error={!!errors['logoUrl']}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Founded Year"
                      name="foundedYear"
                      type="number"
                      value={formData.foundedYear}
                      onChange={handleInputChange}
                      error={!!errors['foundedYear']}
                      helperText={errors['foundedYear']}
                      disabled={isLoading}
                      InputProps={{ inputProps: { min: 1800, max: new Date().getFullYear() } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <LocationSelector
                      value={formData.primaryLocationPK}
                      onChange={(value) => {
                        setFormData({
                          ...formData,
                          primaryLocationPK: value,
                        });

                        // Clear error when field is updated
                        if (errors['primaryLocationPK']) {
                          setErrors({
                            ...errors,
                            primaryLocationPK: undefined,
                          });
                        }
                      }}
                      error={errors['primaryLocationPK']}
                      disabled={isLoading}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>

          {/* Business Address - only show in edit mode */}
          {isEdit && (
            <>
              <Divider sx={{ my: 2 }} />

              <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Business Address
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      name="businessAddress.street"
                      value={formData.businessAddress.street}
                      onChange={handleInputChange}
                      error={!!errors['businessAddress.street']}
                      helperText={errors['businessAddress.street']}
                      disabled={isLoading}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="City"
                      name="businessAddress.city"
                      value={formData.businessAddress.city}
                      onChange={handleInputChange}
                      error={!!errors['businessAddress.city']}
                      helperText={errors['businessAddress.city']}
                      disabled={isLoading}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="State"
                      name="businessAddress.state"
                      value={formData.businessAddress.state}
                      onChange={handleInputChange}
                      error={!!errors['businessAddress.state']}
                      helperText={errors['businessAddress.state']}
                      disabled={isLoading}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      name="businessAddress.zip"
                      value={formData.businessAddress.zip}
                      onChange={handleInputChange}
                      error={!!errors['businessAddress.zip']}
                      helperText={errors['businessAddress.zip']}
                      disabled={isLoading}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublisherForm;
